import {
    CardGiftcardRounded,
    PlayCircleFilledWhiteOutlined,
    ReplyRounded,
    ThumbUpOutlined,
    ThumbUpRounded,
    YouTube,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    CardActions,
    CardContent,
    Grid,
    IconButton,
    Modal,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ShowMoreText from 'react-show-more-text';
import { useToggleLikeMutation } from '../../api/feedSlice';
import fbThumbnail1 from '../../assets/fbThumbnail1.jpeg';
import fbThumbnail2 from '../../assets/fbThumbnail2.jpeg';
import fbThumbnail3 from '../../assets/fbThumbnail3.jpeg';
import LightboxModal from '../../components/export/LightboxModal';
import AudioPlayer from '../../components/utilities/AudioPlayer';
import useAuth from '../../hooks/useAuth';
import useSettings from '../../hooks/useSettings';
import { ModalRequireAuth } from '../../screens/guest/ChurchFinderTab';
import { FeedMedia } from '../../tabs/FeedTab';
import { fbEmbed, isYoutube, y2Embed, y2Thmbnail } from '../../utils/embed';
import { handleGuestFeedShare } from '../../utils/sharePost';

export const fbThumbnail = [fbThumbnail1, fbThumbnail2, fbThumbnail3][
    Math.floor(Math.random() * 3)
];

export default function GuestFeedItem({ feed, type }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [openLightbox, setOpenLightbox] = useState(false);
    const [selectedImage, setSelectedImage] = useState(0);
    const [reqAuthOpen, setReqAuthOpen] = useState(false);
    const [liked, setLiked] = useState(feed?.interactions?.liked);
    const [likes, setLikes] = useState(feed?.interactions?.likes);

    const { layout } = useSettings();
    const { isLoggedIn } = useAuth();
    const navigate = useNavigate();
    const state = useSelector((st) => st);

    const spaced = layout === 'spaced';
    const { churches } = state.list2;

    const [toggleLike] = useToggleLikeMutation();

    const onLikeFeed = async () => {
        if (!isLoggedIn) {
            setReqAuthOpen(true);
            return;
        }

        liked ? setLikes(likes - 1) : setLikes(likes + 1);
        setLiked(!liked);

        await toggleLike({ id: feed?.id, type }).catch(() => {
            liked ? setLikes(likes - 1) : setLikes(likes + 1);
            setLiked(!liked);
        });
    };

    const classes = useStyles();

    const {
        id: feedId,
        title,
        name,
        church,
        date,
        description,
        stream,
        audio,
        devotion,
        member: feedMember,
        dates,
        post,
        media,
        pastor,
    } = feed;

    const member = feedMember || pastor;
    const noChurch = churches?.find(({ id }) => id == church?.id) === undefined;

    const imagesLightbox = media
        ?.filter(({ type: t }) => t === 'image')
        ?.map(({ link }) => link);

    return (
        <>
            <ModalRequireAuth
                open={reqAuthOpen}
                onCancel={() => setReqAuthOpen(false)}
            />
            <Grid item xs={12} className={classes.grid}>
                <div className={`${spaced && 'd-flex'} px-2 py-3`}>
                    {!!spaced && <DoubleAvatar />}
                    <div style={{ flex: 1 }}>
                        <CardContent className="py-0">
                            <div className={`${!spaced && 'd-flex'}`}>
                                {!spaced && <DoubleAvatar feed={feed} />}
                                <div className={`${!spaced && 'ms-2'}`}>
                                    <Typography className="fw-bold text-capitalize">
                                        {noChurch
                                            ? member?.name?.toLowerCase()
                                            : church?.name?.toLowerCase()}
                                    </Typography>
                                    {member?.name && (
                                        <Typography
                                            color="InactiveCaptionText"
                                            className="text-capitalize"
                                        >
                                            {noChurch
                                                ? church?.name?.toLowerCase() ||
                                                  'Online Minister'
                                                : member?.name?.toLowerCase()}{' '}
                                            .{' '}
                                            <Typography
                                                component="span"
                                                className="text-lowercase"
                                                variant="body2"
                                            >
                                                {date
                                                    ? moment(date).fromNow()
                                                    : moment(
                                                          dates?.updated
                                                      ).fromNow()}
                                            </Typography>
                                        </Typography>
                                    )}
                                    {!member?.name && (
                                        <Typography
                                            color="InactiveCaptionText"
                                            className="text-lowercase mb-2"
                                            variant="body2"
                                        >
                                            {date
                                                ? moment(date).fromNow()
                                                : moment(
                                                      dates?.updated
                                                  ).fromNow()}
                                        </Typography>
                                    )}
                                </div>
                            </div>
                            <Typography
                                style={{ textTransform: 'capitalize' }}
                                className="fw-bold mt-2 mb-1"
                            >
                                {(title || name)?.toLowerCase()}
                            </Typography>
                            <ShowMoreText lines={2} more="more" less="less">
                                {description || devotion || post || ' '}
                            </ShowMoreText>
                            <div className="mb-3"></div>

                            {media && (
                                <Box
                                    onClick={() =>
                                        media && setOpenLightbox(true)
                                    }
                                >
                                    <FeedMedia media={media} />
                                </Box>
                            )}

                            {stream && (
                                <div
                                    className={classes.stream}
                                    style={{
                                        backgroundImage: isYoutube(stream)
                                            ? `url(${y2Thmbnail(stream)})`
                                            : `url(${fbThumbnail})`,
                                        height: spaced
                                            ? ((window.innerWidth - 112) * 9) /
                                              16
                                            : ((window.innerWidth - 64) * 9) /
                                              16,
                                    }}
                                >
                                    <IconButton
                                        onClick={() => setModalOpen(true)}
                                    >
                                        {isYoutube(stream) ? (
                                            <YouTube
                                                color="secondary"
                                                className={classes.icon}
                                            />
                                        ) : (
                                            <PlayCircleFilledWhiteOutlined
                                                sx={{ color: 'white' }}
                                                className={classes.icon}
                                            />
                                        )}
                                    </IconButton>
                                </div>
                            )}
                            {audio && <AudioPlayer bR audio={audio} />}
                            <Stack
                                sx={{ mt: 1 }}
                                className="align-items-center justify-content-between"
                                direction="row"
                            >
                                <div
                                    onClick={() =>
                                        navigate(
                                            `/guest-feed/${feedId}?type=${type}`
                                        )
                                    }
                                    className="d-flex align-items-center"
                                >
                                    <ThumbUpRounded
                                        className="text-white"
                                        sx={{
                                            mr: 0.5,
                                            width: 16,
                                            height: 16,
                                            bgcolor: 'primary.main',
                                            padding: 0.2,
                                            borderRadius: 1,
                                        }}
                                    />
                                    <Typography variant="body2">
                                        {likes || 0}
                                    </Typography>
                                </div>

                                <Typography variant="body2">
                                    0 Shares
                                </Typography>
                            </Stack>
                        </CardContent>
                        <CardActions className="justify-content-between">
                            <Button
                                fullWidth
                                sx={{ mx: 0 }}
                                color="inherit"
                                size="small"
                                className="w-100 my-0 mt-2"
                                onClick={onLikeFeed}
                                startIcon={
                                    liked ? (
                                        <ThumbUpRounded color="primary" />
                                    ) : (
                                        <ThumbUpOutlined />
                                    )
                                }
                            >
                                Like
                            </Button>
                            <Button
                                fullWidth
                                color="inherit"
                                size="small"
                                className="w-100 my-0 mt-2"
                                onClick={() => navigate('/donate')}
                                startIcon={<CardGiftcardRounded />}
                                sx={{ mx: 0 }}
                            >
                                Donate
                            </Button>
                            <Button
                                fullWidth
                                color="inherit"
                                size="small"
                                className="w-100 my-0 mt-2"
                                onClick={() => handleGuestFeedShare(feed, type)}
                                startIcon={
                                    <ReplyRounded
                                        style={{ transform: 'rotateY(180deg)' }}
                                    />
                                }
                                sx={{ mx: 0 }}
                            >
                                Share
                            </Button>
                        </CardActions>
                    </div>
                </div>

                <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                    <div className={classes.modal}>
                        <iframe
                            className={classes.iframe}
                            src={
                                isYoutube(stream)
                                    ? y2Embed(stream)
                                    : fbEmbed(stream)
                            }
                            title={title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                </Modal>
            </Grid>

            {media && (
                <LightboxModal
                    images={imagesLightbox}
                    mainSrc={imagesLightbox[selectedImage]}
                    photoIndex={selectedImage}
                    setPhotoIndex={setSelectedImage}
                    isOpen={openLightbox}
                    onCloseRequest={() => setOpenLightbox(false)}
                />
            )}
        </>
    );
}

const DoubleAvatar = ({ feed }) => {
    const navigate = useNavigate();
    const state = useSelector((st) => st);
    const theme = useTheme();

    const { churches } = state.list2;
    const classes = useStyles();

    const { church, member: feedMember, audience, pastor } = feed;

    const member = feedMember || pastor;
    const noChurch = churches?.find(({ id }) => id == church?.id) === undefined;

    return (
        <div>
            <Avatar
                sx={{ border: `1px solid ${theme.palette.divider}` }}
                src={
                    noChurch
                        ? member?.avatar
                        : churches?.find(({ id }) => id == church?.id)?.avatar
                }
                onClick={() => {
                    !noChurch &&
                        navigate(`/church/${church?.id}`, {
                            state: { id: church?.id, name: church?.name },
                        });

                    noChurch &&
                        !church?.name &&
                        navigate(`/minister/${pastor?.username}`, {
                            state: { id: pastor?.id, name: pastor?.name },
                        });
                }}
            >
                {church?.name?.slice(0, 2)}
            </Avatar>

            <div className="d-flex justify-content-end">
                {audience === 'Public' && (
                    <Avatar
                        className={classes.memberAvatar}
                        src={member?.avatar}
                    >
                        {member?.name.slice(0, 2)}
                    </Avatar>
                )}
            </div>
        </div>
    );
};

const useStyles = makeStyles((theme) => ({
    grid: {
        borderTopWidth: 2,
        borderTopColor: theme.palette.divider,
        borderTopStyle: 'solid',
    },
    memberAvatar: {
        width: 22,
        height: 22,
        position: 'relative',
        top: -20,
        border: `1px solid ${theme.palette.background.default}`,
    },
    stream: {
        width: '100%',
        borderRadius: theme.shape.borderRadius,
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,.3)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'soft-light',
    },
    icon: {
        width: 62,
        height: 62,
    },
    modal: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        bgcolor: 'background.paper',
    },
    iframe: {
        width: window.innerWidth,
        height: window.innerWidth * (9 / 16),
        backgroundColor: theme.palette.background.default,
    },
}));
